import React from 'react';
import { IconDownload } from '@tabler/icons-react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import { DocumentVersionDto } from 'src/api/FiretrolApiClient';
import { useDownloadDocumentVersion } from 'src/api/queries/documents/useDocumentDownload';
import { formatDateTime } from 'src/utils';

export interface IDocumentVersionTableProps {
  documents: DocumentVersionDto[];
  jobId: number;
  selectedDocumentId: string | null;
  hasLatest?: boolean;
  emptyText?: string;
}

/** @registry Table of published document versions (number, uploader, date) with a per-row download; `hasLatest` routes the row matching `selectedDocumentId` through the document download endpoint instead of the version one. */
export const DocumentVersionTable: React.FC<IDocumentVersionTableProps> = ({
  documents,
  emptyText = 'No Versions',
  selectedDocumentId,
  jobId,
  hasLatest = false,
}) => {
  const downloadVersion = useDownloadDocumentVersion();

  const handleDownload = (row: DocumentVersionDto) =>
    downloadVersion.mutate({
      jobId,
      versionId: row.id,
      isCurrentDocument: hasLatest && row.id === selectedDocumentId,
    });

  if (documents.length === 0) {
    return <EmptyState description={emptyText} />;
  }

  return (
    <Table highlightOnHover maw={600}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ width: 80 }}>Actions</Table.Th>
          <Table.Th>Version</Table.Th>
          <Table.Th>Uploader</Table.Th>
          <Table.Th>Date</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {documents.map((document, index) => (
          <Table.Tr key={document.id}>
            <Table.Td>
              <Group gap="xs">
                <BasicActionIcon
                  onClick={() => handleDownload(document)}
                  label="Download"
                  color="blue"
                >
                  <IconDownload size={16} />
                </BasicActionIcon>
              </Group>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{documents.length - 1 - index}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">
                {document.uploadedBy?.fullName ?? 'Unknown'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDateTime(document.updatedAt)}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
